import { watch, onUnmounted } from 'vue'

// Reference-counted so multiple overlapping modals (e.g. a confirm dialog
// opened on top of a form modal) don't unlock scroll when only one closes.
let lockCount = 0
let savedScrollY = 0

function lock() {
  lockCount++
  if (lockCount > 1) return
  savedScrollY = window.scrollY
  // position:fixed (not just overflow:hidden) is required for a reliable lock
  // on iOS Safari, which otherwise still allows background scroll/bounce.
  document.body.style.position = 'fixed'
  document.body.style.top = `-${savedScrollY}px`
  document.body.style.left = '0'
  document.body.style.right = '0'
  document.body.style.width = '100%'
}

function unlock() {
  lockCount = Math.max(0, lockCount - 1)
  if (lockCount > 0) return
  document.body.style.position = ''
  document.body.style.top = ''
  document.body.style.left = ''
  document.body.style.right = ''
  document.body.style.width = ''
  window.scrollTo(0, savedScrollY)
}

/**
 * Locks page scroll (including iOS Safari's background bounce/scroll) while
 * `isOpen()` returns true. Call once per modal component with a getter for
 * its own visibility, e.g. `useBodyScrollLock(() => props.show)`.
 */
export function useBodyScrollLock(isOpen: () => boolean) {
  let locked = false

  watch(
    isOpen,
    (open) => {
      if (open && !locked) {
        locked = true
        lock()
      } else if (!open && locked) {
        locked = false
        unlock()
      }
    },
    { immediate: true },
  )

  onUnmounted(() => {
    if (locked) {
      locked = false
      unlock()
    }
  })
}
